import { useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import { Input } from "components/ui/inputs/Input"
import { useUpdateEref } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { applyDisconnectedEcallsToCache, spliceErefIntoCache } from "helpers/quotationCache.helper"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { erefQueueKey } from "components/ui/customWebviewer/ReferenceSidebar/erefQueueKey"
import type { TErefWithFurniture } from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"
import style from "./ElevationName.module.scss"

const { wrapper, label, input } = style

// The endpoint caps `elevation_name` at 128 characters; stop the value at the source.
const MAX_ELEVATION_NAME_LENGTH = 128

/** Blank means "no name": the endpoint takes `null` for that and rejects an empty string. */
const toPayload = (value: string): string | null => value.trim() || null

type Props = { eref: TErefWithFurniture }

/**
 * The eref's elevation name (REQ-474), shown under its number. Commits on blur / Enter through the
 * eref's own mutation queue — same pattern as `TimesReferenced`, and likewise no saving skeleton.
 */
export const ElevationName = ({ eref }: Props) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync: updateErefAsync } = useUpdateEref()
	const { enqueue, versionRef, isPending } = useEntityMutationQueue(erefQueueKey(eref.id), eref.version)

	const serverValue = eref.elevation_name ?? null
	// Last value the server confirmed, and the last one committed or queued (what the field ends at).
	const savedRef = useRef<string | null>(serverValue)
	const targetRef = useRef<string | null>(serverValue)
	const erefIdRef = useRef(eref.id)
	const prevServerRef = useRef(serverValue)
	const [value, setValue] = useState(serverValue ?? "")

	// Reseed on another eref, or when the server value itself changes and isn't this field's own echo —
	// never while this eref's saves are queued. `isPending` clears a render before the spliced value
	// arrives, so its change alone must not count as a server change.
	useEffect(() => {
		const isOtherEref = erefIdRef.current !== eref.id
		const isServerChange = prevServerRef.current !== serverValue
		erefIdRef.current = eref.id
		prevServerRef.current = serverValue
		if (!isOtherEref && (!isServerChange || isPending || serverValue === savedRef.current)) return
		savedRef.current = serverValue
		targetRef.current = serverValue
		setValue(serverValue ?? "")
	}, [eref.id, serverValue, isPending])

	const saveHandle = () => {
		const next = toPayload(value)
		setValue(next ?? "")
		if (next === targetRef.current) return

		targetRef.current = next
		enqueue(async () => {
			try {
				const response = await updateErefAsync({
					quotationId,
					erefId: eref.id,
					body: { version: versionRef.current, elevation_name: next },
				})
				const updated = response?.data
				if (!updated) return
				const { disconnected_ecalls = [], disconnected_ecall_elevations = [], ...erefData } = updated
				versionRef.current = erefData.version
				savedRef.current = erefData.elevation_name ?? null
				queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", quotationId], (cached) => {
					const nextCache = spliceErefIntoCache(cached, erefData)
					return applyDisconnectedEcallsToCache(nextCache, disconnected_ecalls, disconnected_ecall_elevations)
				})
			} catch (e) {
				formatError(e)
				// A rejected name must not stay on screen as if it were saved.
				targetRef.current = savedRef.current
				setValue(savedRef.current ?? "")
				queryClient.invalidateQueries({ queryKey: ["quotation", quotationId] })
			}
		})
	}

	return (
		<div className={wrapper}>
			<span className={label}>Elevation name</span>
			<Input
				className={input}
				value={value}
				placeholder="Add elevation name"
				onChange={(e) => setValue(e.target.value.slice(0, MAX_ELEVATION_NAME_LENGTH))}
				onBlur={saveHandle}
				onPressEnter={saveHandle}
			/>
		</div>
	)
}
